import { z } from 'zod';

import { resolveGroundedOffer } from './artifact-grounding';
import {
  navigateConfirmResult,
  navigateUnknownPageResult,
} from './tool-result-copy';
import type { ClientToolDefinition } from './types';

// `narration` is optional because `run` only reads it behind a truthiness
// guard. `.passthrough()`, on every tool schema, keeps fields the schema does
// not declare, so an unknown one can never be why a call fails.
const schema = z
  .object({
    optionId: z.string(),
    narration: z.string().optional(),
    filterArtifactId: z.string().optional(),
  })
  .passthrough();

/**
 * Terminal tool: NEVER drive the router here. The assistant navigates only
 * after an explicit user click, so this appends a confirmation chip and ends
 * the turn; the chip's click handler does the guarded navigation.
 */
export const navigateTool: ClientToolDefinition<typeof schema> = {
  schema,
  // The offer text is the validated `narration` param this handler appends, so
  // streamed text is a redundant preview.
  narrationPolicy: 'drop',
  run({ optionId, narration, filterArtifactId }, { artifacts, deps }) {
    // A scope where nothing resolved must not open the page's defaults. A
    // partly resolved one links what applied, the scope a read answered on;
    // a Discovery artifact has its own shape and passes as resolved.
    const { grounded: navGrounded, filtersUnresolved: navFiltersUnresolved } =
      resolveGroundedOffer(artifacts, filterArtifactId);
    if (navFiltersUnresolved) {
      return {
        resultForModel:
          'ERROR: the page offer could not preserve the resolved filters. No link was created. Explain that limitation; do not offer the unfiltered page as the answer.',
      };
    }
    const result = deps.requestNavigateConfirm({
      optionId,
      // No entityType: the click path infers the command owner from the path.
      ...(navGrounded !== undefined
        ? { withContinuation: false, resolvedFilters: navGrounded }
        : {}),
    });
    if ('unknownPage' in result) {
      return navigateUnknownPageResult(optionId);
    }
    // An `[phrase](offer:nav)` marker becomes an inline trigger and suppresses
    // the now-redundant chip; a markerless narration falls back to the chip.
    if (narration) {
      deps.appendAssistantText(narration, { nav: result.messageId });
    }
    return navigateConfirmResult(result.pageName);
  },
};
